import React, { useEffect, useRef, useState } from "react";
import { Undo2 } from "lucide-react";
import Lenis from "lenis";
import { AnimatePresence, motion, useScroll, useSpring } from "framer-motion";
import "@/App.css";
import { LangProvider } from "@/context/LangContext";
import CustomCursor from "@/components/CustomCursor";
import SideDock from "@/components/SideDock";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Work from "@/components/Work";
import Immersive from "@/components/Immersive";
import Laduree from "@/components/Laduree";
import MusicVideos from "@/components/MusicVideos";
import AllVideos from "@/components/AllVideos";
import Photography from "@/components/Photography";
import About from "@/components/About";
import Services from "@/components/Services";
import Pricing from "@/components/Pricing";
import Instagram from "@/components/Instagram";
import Contact from "@/components/Contact";
import ChatWidget from "@/components/ChatWidget";
import PageFooter from "@/components/PageFooter";
import HomeButton, { BackButton, TopButton } from "@/components/HomeButton";
import SwipeHint from "@/components/SwipeHint";
import MusicPlayer from "@/components/MusicPlayer";
import Booking from "@/components/Booking";
import AdminPage from "@/components/AdminPage";
import FloatingArrow3D from "@/components/FloatingArrow3D";
import EggArrows from "@/components/EggArrows";
import Radio from "@/components/Radio";
import Portal from "@/components/Portal";
import Culture from "@/components/Culture";
import VipTicket from "@/components/VipTicket";
import ArtistPage from "@/components/ArtistPage";
import OfferPage from "@/components/OfferPage";
import PressKitPage from "@/components/PressKitPage";
import FaqPage from "@/components/FaqPage";

// lit le slug du press kit au moment du rendu (deep-link #presskit?artist=<slug>)
function PressKitRoute() {
    return <PressKitPage slug={window.__presskitSlug} onBack={() => window.__goPage?.("artiste")} />;
}
import { auth } from "@/lib/auth";
import { useEffect as useEff } from "react";

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    render() {
        if (this.state.error) {
            return (
                <div className="flex min-h-screen items-center justify-center bg-ink font-mono text-xs tracking-[0.3em] text-cream">
                    SOMETHING WENT WRONG.
                </div>
            );
        }
        return this.props.children;
    }
}

function ScrollProgress() {
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
    return (
        <motion.div
            data-testid="scroll-progress"
            style={{ scaleX }}
            className="fixed inset-x-0 top-0 z-[70] h-[2px] origin-left bg-gradient-to-r from-violet via-blood to-emerald"
        />
    );
}

function Curtain() {
    const [done, setDone] = useState(false);
    useEffect(() => {
        const t = setTimeout(() => setDone(true), 2400);
        return () => clearTimeout(t);
    }, []);
    return (
        <AnimatePresence>
            {!done && (
                <motion.div
                    data-testid="load-curtain"
                    className="fixed inset-0 z-[95] overflow-hidden"
                >
                    {/* cinema panels — slide open like a shutter */}
                    <motion.div
                        exit={{ y: "-100%" }}
                        transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
                        className="absolute inset-x-0 top-0 h-1/2 bg-ink"
                    />
                    <motion.div
                        exit={{ y: "100%" }}
                        transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
                        className="absolute inset-x-0 bottom-0 h-1/2 bg-ink"
                    />
                    {/* cinematic content — fades before the panels split */}
                    <motion.div
                        className="absolute inset-0"
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                    >
                        {/* shutter light line */}
                        <motion.div
                            initial={{ scaleX: 0, opacity: 1 }}
                            animate={{ scaleX: 1, opacity: [1, 1, 0] }}
                            transition={{ duration: 1.4, times: [0, 0.75, 1], ease: [0.16, 1, 0.3, 1] }}
                            className="absolute left-0 top-1/2 h-px w-full bg-gradient-to-r from-transparent via-violet to-transparent"
                        />
                        {/* violet glow pulse */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.5 }}
                            animate={{ opacity: [0, 0.7, 0.3], scale: [0.5, 1.15, 1] }}
                            transition={{ duration: 1.9, ease: "easeOut" }}
                            className="absolute left-1/2 top-1/2 h-80 w-80 -translate-x-1/2 -translate-y-1/2 rounded-full"
                            style={{ background: "radial-gradient(circle, rgba(138,43,226,0.4) 0%, transparent 70%)" }}
                        />
                        {/* logo reveal */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                            <motion.div
                                initial={{ opacity: 0, letterSpacing: "0.2em", filter: "blur(12px)" }}
                                animate={{ opacity: 1, letterSpacing: "0.55em", filter: "blur(0px)" }}
                                transition={{ duration: 1.4, delay: 0.35, ease: "easeOut" }}
                                className="font-mono text-[12px] text-cream md:text-sm"
                            >
                                GLOOM ENT. ®
                            </motion.div>
                            <motion.div
                                initial={{ scaleX: 0 }}
                                animate={{ scaleX: 1 }}
                                transition={{ duration: 1.1, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
                                className="h-px w-24 bg-gold/70"
                            />
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

const HomePage = () => (
    <>
        <Immersive />
        <Hero />
        <Marquee />
        <Work />
        <Laduree />
        <MusicVideos />
        <Photography />
        <About />
        <Services />
        <Pricing />
        <Instagram />
        <Contact />
    </>
);

const VideosPage = () => <AllVideos />;

const PhotoPage = () => (
    <>
        <Photography />
        <About />
        <Instagram />
    </>
);

const ContactPage = () => (
    <>
        <Services />
        <Pricing />
        <Booking />
        <Contact />
    </>
);

export const PAGES = [
    { id: "home", label: { en: "HOME", fr: "ACCUEIL" }, num: "01", node: <HomePage /> },
    { id: "videos", label: { en: "VIDEOS", fr: "VIDÉOS" }, num: "02", node: <VideosPage /> },
    { id: "photo", label: { en: "PHOTO", fr: "PHOTO" }, num: "03", node: <PhotoPage /> },
    { id: "contact", label: { en: "TARIFS & CONTACT", fr: "TARIFS & CONTACT" }, num: "04", node: <ContactPage /> },
    { id: "radio", label: { en: "GLM RADIO", fr: "GLM RADIO" }, num: "05", node: <Radio /> },
    { id: "culture", label: { en: "CULTURE", fr: "CULTURE" }, num: "06", node: <Culture /> },
    { id: "artiste", label: { en: "ARTISTS", fr: "ARTISTES" }, num: "07", node: <ArtistPage /> },
    { id: "vip", label: { en: "VIP", fr: "VIP" }, num: "08", node: <VipTicket /> },
    { id: "offre", label: { en: "OFFER", fr: "OFFRE" }, num: "09", node: <OfferPage /> },
    { id: "presskit", label: { en: "PRESS KIT", fr: "PRESS KIT" }, num: "11", node: <PressKitRoute /> },
    { id: "faq", label: { en: "FAQ", fr: "FAQ" }, num: "12", node: <FaqPage /> },
    { id: "portal", label: { en: "MY SPACE", fr: "MON ESPACE" }, num: "10", node: <Portal /> },
];

// Apple-style page transition: soft fade + gentle rise, no 3D
const APPLE_EASE = [0.32, 0.72, 0, 1];

// keeps auth state available to SideDock
function AuthBridge({ setAuthed, setIsAdmin }) {
    useEff(() => auth.subscribe((s) => {
        setAuthed(s.status === "authed");
        setIsAdmin(!!s.user?.is_admin);
    }), [setAuthed, setIsAdmin]);
    return null;
}

// Retour Stripe — ?payment=success&session_id=… / ?payment=cancel
function PaymentReturn() {
    const [info, setInfo] = useState(null);
    useEffect(() => {
        const q = new URLSearchParams(window.location.search);
        const payment = q.get("payment");
        const sessionId = q.get("session_id");
        if (!payment) return;
        if (payment === "cancel") {
            setInfo({ state: "cancel" });
            return;
        }
        if (!sessionId) return;
        setInfo({ state: "checking" });
        let tries = 0;
        const poll = async () => {
            try {
                const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`);
                if (r.status === 404) { setInfo({ state: "pending" }); return; }
                const d = await r.json();
                if (d.payment_status === "paid") {
                    setInfo({ state: "paid" });
                    return;
                }
            } catch { /* retry */ }
            if (++tries < 15) setTimeout(poll, 2000);
            else setInfo({ state: "pending" });
        };
        poll();
    }, []);
    if (!info) return null;
    const close = () => {
        window.history.replaceState({}, "", window.location.pathname);
        setInfo(null);
    };
    return (
        <div data-testid="payment-return" className="fixed inset-0 z-[95] flex items-center justify-center bg-ink/85 p-5 backdrop-blur-md">
            <div className="max-w-md border border-gold/50 bg-ink p-8 text-center">
                {info.state === "paid" && (
                    <>
                        <p className="font-display text-3xl font-extrabold uppercase tracking-tight text-gold">Acompte reçu.</p>
                        <p className="mt-3 font-mono text-[10px] leading-relaxed tracking-[0.2em] text-cream/60">
                            Votre date est officiellement bloquée. Reçu Stripe envoyé par email.
                        </p>
                        <div className="mt-6 space-y-3 border-t border-gold/20 pt-6 text-left">
                            <p className="font-mono text-[9px] tracking-[0.3em] text-gold">PROCHAINES ÉTAPES</p>
                            {[
                                "01 — Nous vous contactons sous 48h pour caler le brief",
                                "02 — Moodboard et plan de tournage validés ensemble",
                                "03 — Le solde est réglé avant le jour J",
                            ].map((s) => (
                                <p key={s} className="font-mono text-[9px] leading-relaxed tracking-[0.2em] text-cream/55">{s}</p>
                            ))}
                        </div>
                    </>
                )}
                {info.state === "checking" && (
                    <p className="font-mono text-[10px] tracking-[0.3em] text-cream/60">VÉRIFICATION DU PAIEMENT…</p>
                )}
                {info.state === "pending" && (
                    <p className="font-mono text-[10px] leading-relaxed tracking-[0.2em] text-cream/60">
                        Paiement en cours de confirmation — vous recevrez la confirmation par email.
                    </p>
                )}
                {info.state === "cancel" && (
                    <p className="font-mono text-[10px] leading-relaxed tracking-[0.2em] text-cream/60">
                        Paiement annulé — votre date reste réservée 48h. Réessayez le paiement par carte depuis votre confirmation de réservation.
                    </p>
                )}
                <button
                    data-testid="payment-return-close"
                    onClick={close}
                    className="mt-6 border border-cream/30 px-6 py-2.5 font-mono text-[9px] tracking-[0.3em] text-cream/70 transition-colors hover:border-gold hover:text-gold"
                >
                    FERMER
                </button>
            </div>
        </div>
    );
}

function App() {
    const [page, setPage] = useState(() => {
        // deep-link : #<pageId> (ex. #artiste?artist=sijay) ouvre la page directement
        const raw = window.location.hash.replace("#", "");
        const h = raw.split("?")[0];
        if (h === "presskit") {
            // #presskit?artist=<slug> — press kit public partageable
            window.__presskitSlug = new URLSearchParams(raw.split("?")[1] || "").get("artist");
        }
        // #artiste?creator=6xo — lien direct vers le profil créateur
        if (h === "artiste" && new URLSearchParams(raw.split("?")[1] || "").get("creator")) {
            window.__scrollToCreator = true;
        }
        // #reset?token=… — réinitialisation du mot de passe → page Mon Espace
        if (h === "reset") {
            window.__resetToken = new URLSearchParams(raw.split("?")[1] || "").get("token");
            return "portal";
        }
        return PAGES.some((p) => p.id === h) ? h : PAGES[0].id;
    });
    const [adminOpen, setAdminOpen] = useState(() => window.location.hash === "#admin");
    const [authed, setAuthed] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const navStack = useRef([]);
    const [canGoBack, setCanGoBack] = useState(false);

    useEffect(() => {
        const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
        window.__lenis = lenis;
        let frame;
        const loop = (time) => {
            lenis.raf(time);
            frame = requestAnimationFrame(loop);
        };
        frame = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(frame);
            lenis.destroy();
            window.__lenis = null;
        };
    }, []);

    const go = (id) => {
        const idx = PAGES.findIndex((p) => p.id === id);
        if (idx < 0) return;
        if (id === page) {
            // already on this page (e.g. logo click on home) → just scroll to top
            window.__lenis?.scrollTo(0, { duration: 1.2, force: true });
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        // remember where we were — RETOUR brings the visitor back to this exact spot
        navStack.current.push({ id: page, y: window.scrollY });
        setCanGoBack(true);
        setPage(id);
        window.__lenis?.scrollTo(0, { immediate: true });
        window.scrollTo(0, 0);
    };

    const goBack = () => {
        const prev = navStack.current.pop();
        if (!prev) return;
        setCanGoBack(navStack.current.length > 0);
        setPage(prev.id);
        const restore = () => {
            window.__lenis?.stop();
            window.scrollTo(0, prev.y);
            window.__lenis?.scrollTo(prev.y, { immediate: true, force: true });
            window.__lenis?.start();
        };
        // the home page keeps growing while sections mount — fixed delays race with
        // Framer animations. Poll scrollHeight until it stops changing, THEN restore.
        let lastH = -1;
        let stable = 0;
        const started = performance.now();
        const poll = () => {
            const h = document.documentElement.scrollHeight;
            if (h === lastH) stable += 1;
            else stable = 0;
            lastH = h;
            const elapsed = performance.now() - started;
            if ((stable >= 3 && elapsed > 500) || elapsed > 2500) {
                restore();
                setTimeout(restore, 350); // safety re-apply after Lenis restarts
                return;
            }
            setTimeout(poll, 120);
        };
        setTimeout(poll, 150);
    };

    useEffect(() => {
        window.__goPage = go;
        // marqueur de scroll interne (ex. « voir plus ») — RETOUR ramène à l'endroit exact
        window.__pushNavMark = () => {
            navStack.current.push({ id: page, y: window.scrollY });
            setCanGoBack(true);
        };
        return () => {
            window.__goPage = null;
            window.__pushNavMark = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    // RETOUR toujours disponible : page précédente (position restaurée) ou haut de page
    const handleBack = () => {
        if (canGoBack) {
            goBack();
        } else {
            window.__lenis?.scrollTo(0, { duration: 1.2, force: true });
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    // geste mobile natif : glisser depuis le bord gauche vers la droite = RETOUR
    const [swipeX, setSwipeX] = useState(0);
    useEffect(() => {
        // les événements tactiles ne se déclenchent que sur écran tactile —
        // pas de garde pointer:coarse (émulateurs inclus)
        let startX = 0;
        let startY = 0;
        let tracking = false;
        const onStart = (e) => {
            const t = e.touches && e.touches[0];
            if (!t) return;
            if (t.clientX <= 36) {
                tracking = true;
                startX = t.clientX;
                startY = t.clientY;
            }
        };
        const onMove = (e) => {
            if (!tracking) return;
            const t = e.touches && e.touches[0];
            if (!t) return;
            const dx = t.clientX - startX;
            if (Math.abs(t.clientY - startY) > 70 && dx < 40) {
                // geste vertical → annuler le suivi
                tracking = false;
                setSwipeX(0);
                return;
            }
            if (dx > 0) setSwipeX(Math.min(dx, 120));
        };
        const onEnd = (e) => {
            if (!tracking) return;
            tracking = false;
            const t = e.changedTouches && e.changedTouches[0];
            if (!t) {
                setSwipeX(0);
                return;
            }
            const dx = t.clientX - startX;
            const dy = Math.abs(t.clientY - startY);
            setSwipeX(0);
            if (dx > 80 && dy < 70) handleBack();
        };
        window.addEventListener("touchstart", onStart, { passive: true });
        window.addEventListener("touchmove", onMove, { passive: true });
        window.addEventListener("touchend", onEnd, { passive: true });
        return () => {
            window.removeEventListener("touchstart", onStart);
            window.removeEventListener("touchmove", onMove);
            window.removeEventListener("touchend", onEnd);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canGoBack]);

    // Google OAuth callback: session_id arrives in the URL hash
    useEffect(() => {
        const processCallback = async () => {
            if (window.location.hash.includes("session_id=")) {
                const sessionId = window.location.hash.split("session_id=")[1].split("&")[0];
                try {
                    await auth.exchange(sessionId);
                } catch {
                    // invalid session → stay guest
                }
                window.history.replaceState(null, "", window.location.pathname);
                return;
            }
            // deep link (e.g. direct visit to /#admin): give the browser a beat
            // to send freshly-set cookies before checking, then retry once
            await new Promise((r) => setTimeout(r, 250));
            await auth.check();
            if (auth.state.status === "guest" && window.location.hash === "#admin") {
                await new Promise((r) => setTimeout(r, 700));
                auth.check();
            }
        };
        processCallback();
    }, []);

    // hidden admin area via #admin
    useEffect(() => {
        const onHash = () => {
            const h = window.location.hash;
            setAdminOpen(h === "#admin");
            // routing complet : tout hash valide change la page (liens partagés en session)
            if (h !== "#admin") {
                const raw = h.replace("#", "");
                const id = raw.split("?")[0];
                if (id === "presskit") {
                    window.__presskitSlug = new URLSearchParams(raw.split("?")[1] || "").get("artist");
                }
                // #artiste?creator=6xo — scroll direct sur le profil créateur
                if (id === "artiste" && new URLSearchParams(raw.split("?")[1] || "").get("creator")) {
                    window.__scrollToCreator = true;
                    setTimeout(() => window.dispatchEvent(new Event("creator:scroll")), 120);
                }
                // #reset?token=… — page de réinitialisation dans Mon Espace
                if (id === "reset") {
                    window.__resetToken = new URLSearchParams(raw.split("?")[1] || "").get("token");
                    setPage("portal");
                    return;
                }
                if (PAGES.some((p) => p.id === id)) setPage(id);
            }
        };
        window.addEventListener("hashchange", onHash);
        return () => window.removeEventListener("hashchange", onHash);
    }, []);

    const active = PAGES.find((p) => p.id === page) || PAGES[0];

    return (
        <ErrorBoundary>
            <LangProvider>
                <div className="bg-ink text-cream selection:bg-violet">
                    <CustomCursor />
                    <ScrollProgress />
                    <PaymentReturn />
                    <Curtain />
                    <FloatingArrow3D visible={page === "home" && !adminOpen} />
                    <EggArrows page={page} go={go} />
                    <AuthBridge setAuthed={setAuthed} setIsAdmin={setIsAdmin} />
                    <SideDock
                        page={page}
                        go={go}
                        authed={authed}
                        isAdmin={isAdmin}
                    />
                    {adminOpen ? (
                        <AdminPage />
                    ) : (
                        <>
                            <AnimatePresence mode="wait" initial={false}>
                                <motion.main
                                    key={active.id}
                                    data-testid={`page-${active.id}`}
                                    initial={{ opacity: 0, y: 28, scale: 0.995 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -20, scale: 0.995 }}
                                    transition={{ duration: 0.55, ease: APPLE_EASE }}
                                >
                                    {active.node}
                                </motion.main>
                            </AnimatePresence>
                            <PageFooter page={page} onNav={go} />
                            {/* indicateur du geste retour (bord gauche → droite) */}
                            <div
                                data-testid="swipe-back-indicator"
                                className="pointer-events-none fixed left-0 top-1/2 z-[64] -translate-y-1/2"
                                style={{ opacity: swipeX > 8 ? Math.min(swipeX / 60, 1) : 0, transition: swipeX === 0 ? "opacity 0.2s" : "none" }}
                            >
                                <div
                                    className={`flex h-12 w-12 items-center justify-center rounded-r-full border backdrop-blur-md transition-colors ${
                                        swipeX > 80 ? "border-gold bg-gold/25 text-gold" : "border-white/20 bg-ink/70 text-cream/70"
                                    }`}
                                    style={{ transform: `translateX(${Math.min(swipeX * 0.6, 48)}px)` }}
                                >
                                    <Undo2 size={16} />
                                </div>
                            </div>
                            <div className="fixed bottom-6 left-5 z-[65] flex items-center gap-3 md:bottom-8 md:left-10">
                                <BackButton onBack={handleBack} />
                                <TopButton />
                                <HomeButton page={page} onNav={go} inline />
                            </div>
                        </>
                    )}
                    <MusicPlayer />
                    <SwipeHint />
                    <ChatWidget />
                </div>
            </LangProvider>
        </ErrorBoundary>
    );
}

export default App;
